import type { HekateDisplayForm } from '@onshape/www-inertia';
import { FieldHtmlNameUtil, HekateDisplayField, ErrorTagUtil, Trans } from '@onshape/www-inertia';
import TrackingUtil from '../../../util/TrackingUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import ExternalUtil from '../../../ExternalUtil';
import LocalhostTestingUtil from '../../../util/LocalhostTestingUtil';
import ServiceEndpointsUtil from '../../../util/ServiceEndpointsUtil';
import type { SetupContext } from 'vue';

type RecaptchaCapability = { capable: boolean; href: string; id: string; name: string };
type RecaptchaSiteKeyResponse = { recaptchaSiteKey: string };
export default class RecaptchaCoordinator {
  /** True if this form shows recaptcha */
  private readonly isRecaptcha: Ref<boolean>;

  /** True if the recaptcha has been solved */
  private readonly isRecaptchaSolved: Ref<boolean>;

  /** The element that google populates with recaptcha GUI */
  private readonly recaptchaElement: Ref<null | HTMLElement>;

  /** True while recaptcha loading is timed out */
  private readonly isShowNoRecaptchaError: Ref<boolean>;

  /** True while recaptcha is loading */
  private readonly isRecaptchaLoading: Ref<boolean>;

  private recaptchaSiteKey: Ref<string>;

  private recaptchaCapability: string;

  private context: SetupContext;

  private formDefinition: Ref<HekateDisplayForm | null>;

  private recaptchaVersionField: HekateDisplayField;

  private recaptchaIdField: HekateDisplayField;

  private isRecaptchaV2: Ref<boolean>;

  private isRecaptchaV3: Ref<boolean>;

  private hasFailedRecaptchaV3: Ref<boolean>;

  private isAlreadyLoaded: Ref<boolean>;

  private isRecaptchaV2Capability: boolean;

  constructor({
    isRecaptcha,
    isRecaptchaSolved,
    context,
    formDefinition,
    recaptchaSiteKey,
    isRecaptchaV2,
    isRecaptchaV3,
    hasFailedRecaptchaV3,
  }: {
    isRecaptcha: Ref<boolean>;
    isRecaptchaSolved: Ref<boolean>;
    context: SetupContext;
    formDefinition: Ref<HekateDisplayForm | null>;
    recaptchaSiteKey: Ref<string>;
    isRecaptchaV2: Ref<boolean>;
    isRecaptchaV3: Ref<boolean>;
    hasFailedRecaptchaV3: Ref<boolean>;
  }) {
    this.context = context;
    this.recaptchaElement = ref(null);
    this.isRecaptcha = isRecaptcha;
    this.isRecaptchaSolved = isRecaptchaSolved;
    this.isShowNoRecaptchaError = ref(false);

    this.isRecaptchaLoading = ref(isRecaptcha.value);

    this.recaptchaCapability = '';
    this.recaptchaSiteKey = recaptchaSiteKey;
    this.isRecaptchaV2 = isRecaptchaV2;
    this.isRecaptchaV3 = isRecaptchaV3;
    this.hasFailedRecaptchaV3 = hasFailedRecaptchaV3;

    this.formDefinition = formDefinition;

    if (this.formDefinition.value === null) {
      throw ErrorUtil.internalError({ msg: 'formDefinition is null' });
    }

    let tmpField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition,
      fieldName: FieldHtmlNameUtil.recaptchaVersion,
    });
    if (!tmpField) {
      this.recaptchaVersionField = HekateDisplayField.newHiddenFieldInstance({
        id: 'recaptcha-version',
        htmlName: FieldHtmlNameUtil.recaptchaVersion,
      });
      this.formDefinition.value.displayFields.push(this.recaptchaVersionField);
    } else {
      this.recaptchaVersionField = tmpField;
    }
    tmpField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition,
      fieldName: FieldHtmlNameUtil.recaptchaId,
    });
    if (!tmpField) {
      this.recaptchaIdField = HekateDisplayField.newHiddenFieldInstance({
        id: 'recaptcha-id',
        htmlName: FieldHtmlNameUtil.recaptchaId,
      });
      this.formDefinition.value.displayFields.push(this.recaptchaIdField);
    } else {
      this.recaptchaIdField = tmpField;
    }

    this.isAlreadyLoaded = ref(false);
    this.isRecaptchaV2Capability = false;

    watch(hasFailedRecaptchaV3, () => this.failedV3());
  }

  /** Set the v2 vs v3 related values, by calling the cad capabilities endpoint */
  private async setCapabilities() {
    if (LocalhostTestingUtil.getRecaptchaKeyOrNull()) {
      console.log('localhost testing recaptcha key, using v3');
      this.isRecaptchaV2Capability = false;
      this.recaptchaCapability = 'v3';
      this.recaptchaVersionField.val = 'v3';
      this.isRecaptchaV3.value = true;
      this.isRecaptchaV2.value = false;
      return;
    }

    const cadDomain = ServiceEndpointsUtil.mapDomain('https://cad.onshape.com');
    const v3CapabilityResp = () =>
      fetch(`${cadDomain}/api/capabilities/check/web/GOOGLE_RECAPTCHA_V3_INVITE`).then((resp) =>
        resp.json()
      );
    const v2CapabilityResp = () =>
      fetch(`${cadDomain}/api/capabilities/check/web/GOOGLE_RECAPTCHA_V2_INVITE`).then((resp) =>
        resp.json()
      );

    const capabilities = await Promise.all([v3CapabilityResp(), v2CapabilityResp()]);
    const v3Capability: RecaptchaCapability = capabilities[0];
    const v2Capability: RecaptchaCapability = capabilities[1];

    this.isRecaptchaV2Capability = v2Capability.capable;

    this.recaptchaCapability = v3Capability.capable ? 'v3' : 'v2';
    this.recaptchaVersionField.val = this.recaptchaCapability;

    this.isRecaptchaV3.value = this.recaptchaCapability === 'v3';
    this.isRecaptchaV2.value = this.recaptchaCapability === 'v2';
  }

  /** Set the recapatcha key */
  private async setRecaptchaKey() {
    const testingKey = LocalhostTestingUtil.getRecaptchaKeyOrNull();
    if (testingKey) {
      this.recaptchaSiteKey.value = testingKey;
      return;
    }
    const cadDomain = ServiceEndpointsUtil.mapDomain('https://cad.onshape.com');
    const url = `${cadDomain}/api/clientinfo/recaptcha/invite`;

    const recaptchaKeyResponse: RecaptchaSiteKeyResponse = await fetch(url).then((resp) =>
      resp.json()
    );

    this.recaptchaSiteKey.value = recaptchaKeyResponse.recaptchaSiteKey;
  }

  /** Load the recaptcha script tag */
  private static async loadRecaptchaScriptTag() {
    if (window.onloadRecaptchaCallback) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateRecaptcha,
        msg: 'recaptcha already loaded in loadRecaptcha',
      });
      return;
    }

    let resRecaptcha: (_v: unknown) => void;
    window.onloadRecaptchaCallback = () => {
      resRecaptcha('ready');
    };

    ExternalUtil.createScriptTag({
      src: 'https://www.google.com/recaptcha/api.js?onload=onloadRecaptchaCallback&render=explicit',
      className: 'grecaptcha-loading',
      isAsync: false,
    });

    // wait for recaptcha code to run and onloadRecaptachaCallback to be called
    await new Promise((res) => {
      resRecaptcha = res;
    });

    if (window.grecaptcha === null) {
      throw ErrorUtil.internalError({ msg: ' grecaptcha is null' });
    }
  }

  private loadRecaptchaV3() {
    if (window.grecaptcha === null) {
      throw ErrorUtil.internalError({ msg: ' grecaptcha is null' });
    }

    const recaptchaId = window.grecaptcha.render('hk-recaptcha-element-v3', {
      sitekey: this.recaptchaSiteKey.value,
      badge: 'bottomleft',
      size: 'invisible',
    });

    this.recaptchaIdField.val = recaptchaId;
    this.isRecaptchaSolved.value = true;
  }

  private loadRecaptchaV2() {
    if (!window.grecaptcha) {
      throw ErrorUtil.internalError({ msg: ' grecaptcha is null' });
    }

    window.grecaptcha.render('hk-recaptcha-element-v2', {
      sitekey: this.recaptchaSiteKey.value,
      callback: (v: string) => {
        let val: string;
        if (new URL(document.location.href).searchParams.get('poison-recaptcha') === 'v2') {
          val = 'poison-v2';
        } else {
          val = v;
        }
        // THis is where the recaptcha field is filled out
        this.context.emit('update:modelValue', val);
        this.isRecaptchaSolved.value = true;
      },
      'expired-callback': () => {
        this.isRecaptchaSolved.value = false;
      },
      'error-callback': () => {
        this.isRecaptchaSolved.value = false;
        ErrorUtil.safePostMessageToSentry({
          tag: ErrorTagUtil.hekateRecaptcha,
          msg: 'Recaptcha error callback called',
        });
      },
    });
  }

  private async loadRecaptcha(elm: Ref<HTMLElement | null>) {
    if (!this.isRecaptcha.value) {
      return;
    }

    if (elm.value === null) {
      setTimeout(() => {
        this.loadRecaptcha(elm);
      }, 300);
      return;
    }

    // Wait and if we don't have the recaptcha yet show an error
    let isHaveRecaptcha = false;
    setTimeout(async () => {
      this.isRecaptchaLoading.value = false;
      if (isHaveRecaptcha) {
        return;
      }
      this.isShowNoRecaptchaError.value = true;
    }, 10 * 1000);

    // load the prerequisites
    try {
      await Promise.all([
        this.setCapabilities(),
        this.setRecaptchaKey(),
        RecaptchaCoordinator.loadRecaptchaScriptTag(),
      ]);
    } catch (e) {
      console.error(e);
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateRecaptcha,
        msg: 'recaptcha loading error',
        error: e,
      });
      TrackingUtil.mouseFlowPush(['tag', 'recaptcha-load-fail']);
      return;
    }

    // NOTE if you change this make sure to update `ExternalUtil.safeResetRecaptcha`
    if (this.recaptchaCapability === 'v3') {
      this.loadRecaptchaV3();
    } else {
      this.loadRecaptchaV2();
    }

    isHaveRecaptcha = true;
    this.isRecaptchaLoading.value = false;
  }

  private async failedV3() {
    if (!this.hasFailedRecaptchaV3) return;
    this.isRecaptchaSolved.value = false;
    this.isRecaptchaV3.value = false;
    this.isRecaptchaV2.value = true;
    this.recaptchaCapability = 'v2';
    this.recaptchaVersionField.val = 'v2';

    const v2Element = document.getElementById('hk-recaptcha-element-v2');
    if (v2Element === null) {
      setTimeout(() => {
        this.failedV3();
      }, 300);
      return;
    }

    if (this.formDefinition.value === null) {
      throw ErrorUtil.internalError({ msg: 'formDefinition is null' });
    }
    const failMessage = {
      message: Trans.by(
        'Recaptcha Expired, please solve recaptcha and submit again',
        'recaptchaExpiredError'
      ),
      hint: 'recaptcha-expired',
    };
    this.formDefinition.value.errorMessages.push(failMessage);
    ErrorUtil.addSpecificFormError({
      formDefinition: this.formDefinition,
      errorMessages: [failMessage],
    });
    TrackingUtil.mouseFlowPush(['tag', 'recaptcha-expired']);

    this.loadRecaptchaV2();
  }

  public setup() {
    onMounted(() => {
      ExternalUtil.findDisplayFieldOrThrow({
        formDefinition: this.formDefinition as Ref<HekateDisplayForm>,
        fieldName: FieldHtmlNameUtil.recaptcha,
      }).val = '';

      if (!this.hasFailedRecaptchaV3.value) {
        this.loadRecaptcha(this.recaptchaElement);
      } else {
        this.isRecaptchaLoading.value = false;
        this.failedV3();
      }
    });

    return {
      isRecaptcha: this.isRecaptcha,
      recaptchaElement: this.recaptchaElement,
      isShowNoRecaptchaError: this.isShowNoRecaptchaError,
      isRecaptchaLoading: this.isRecaptchaLoading,
      isRecaptchaV3: this.isRecaptchaV3,
      isRecaptchaV2: this.isRecaptchaV2,
    };
  }
}
